/**
 * Checkout payload — what the client posts to `POST /api/paypal-orders/capture`
 * as `checkoutData`, and (transitively, via the captured-pending recovery row
 * + the reconciliation cron + the Stripe checkout-session draft) what becomes
 * the input to `lib/order-creation.ts::createOrder`.
 *
 * This is the CAPTURE channel — sibling of `CheckoutIntent` (the CREATE
 * channel, see `./checkout-intent.ts`). Pre-MISU these two channels had
 * independent flat interfaces that DRIFTED: `CheckoutIntent` grew an
 * `externalShipping` field on 2026-06-25 (`adfee9da`) for the live-rate
 * rail; `CheckoutPayload` did NOT mirror it. Every live-rate checkout
 * landed with `seller_orders.shipping = 0` and `external_shipment_id =
 * NULL` for the next ~14 hours — see
 * `.tondocs/shippo/HANDOFF_EXTERNAL_SHIPPING_CAPTURE_GAP_2026_06_26.md`.
 *
 * PR-E (Option E2: structural redesign, 2026-06-27): `CheckoutPayload` is
 * now {@link CheckoutIntent} & {@link CaptureSnapshot} per-branch — the two
 * channels are no longer parallel declarations; CAPTURE structurally extends
 * CREATE. Drift on shared (Intent-side) axes is compile-impossible because
 * the Intent half is literally ONE declaration. The {@link
 * CheckoutPayloadExtendsIntent} probe at the bottom of this file is the F2
 * source-of-truth: a future edit that drifts Payload from Intent on a shared
 * field fails tsc red-line at the probe line, BEFORE any boundary parse runs.
 *
 *   - `kind: "flat_rate"` — no `externalShipping` field. Items mirror
 *     {@link CheckoutIntentFlatRateItem} (carries `shippingMethodId` +
 *     `context`) plus the capture-time {@link CheckoutPayloadItemSnapshot}.
 *   - `kind: "live_rate"` — `externalShipping` REQUIRED (mirror of
 *     {@link CheckoutIntentLiveRate}). Items mirror
 *     {@link CheckoutIntentLiveRateItem} plus the snapshot.
 *
 * Why a root-level `kind` and not per-line (mirror of Intent's per-item
 * `kind` pre-Move-2)? Single-seller invariant + `listings.shipping_mode` is
 * per-seller: every item in one checkout shares the same shipping mode.
 *
 * The constant {@link CHECKOUT_ITEM_KINDS} is the SINGLE SOURCE for which
 * shipping modes exist — imported here and on `./checkout-intent.ts`.
 *
 * @see ./checkout-intent.ts — sibling (CREATE channel)
 * @see ./capture-snapshot.ts — the "capture-side" half of the extension
 * @see lib/checkout-payload.ts — `buildCheckoutPayload` (returns this union)
 * @see lib/order-creation.ts — consumer side (`switch (body.kind)`)
 * @see .tondocs/skills/type-driven-design.md §1 (ADT), §2 (illegal-states), §3 (`never`)
 */

import { z } from "zod";
import type { CarrierProvider } from "./carrier";
import {
  CHECKOUT_ITEM_KINDS,
  CheckoutIntentExternalShippingSelectionSchema,
  CheckoutIntentFlatRateItemSchema,
  CheckoutIntentFlatRateSchema,
  CheckoutIntentLiveRateItemSchema,
  CheckoutIntentLiveRateSchema,
  type CheckoutIntentAddress,
  type CheckoutIntentFlatRate,
  type CheckoutIntentFlatRateItem,
  type CheckoutIntentLiveRate,
  type CheckoutIntentLiveRateItem,
} from "./checkout-intent";
import {
  CaptureSnapshotSchema,
  captureSnapshotShape,
  type CaptureSnapshot,
} from "./capture-snapshot";
import { pinned } from "./pin";
import { rawImageRefField, type RawImageRef } from "./raw-image-ref";

/**
 * Per-item flat-rate shipping method snapshot taken at checkout time. The
 * server re-derives shipping via `calculateSellerShippingTotal` from these
 * fields and abandons the row if the buyer-supplied values don't match the
 * listing's `shipping_config` (defence against tampering — same rule as the
 * Stripe checkout-session schema).
 *
 * `null` for live-rate items (the rate is at the order level on the live_rate
 * variant of {@link CheckoutPayload} — not per-line).
 */
export interface CheckoutPayloadSelectedShippingMethod {
  id: string;
  name: string;
  price: number;
  extraValue?: number;
  allowCombinedShipping?: boolean;
}

/**
 * Per-item capture-time snapshot — the fields the buyer-emitted CAPTURE
 * payload carries on EACH line that don't exist on the corresponding Intent
 * line. Combined with `CheckoutIntent*Item` to form
 * `CheckoutPayload*Item`.
 *
 * Snapshot vs intent: the intent only declares "I want N of listing X with
 * shipping method Y". The snapshot is the rich content the buyer saw at
 * checkout time — price, name, image, seller display — captured here so the
 * order row, the buyer's confirmation email and the dispute audit trail
 * read the same values weeks later.
 */
export interface CheckoutPayloadItemSnapshot {
  price: number;
  currency: string;
  name: string;
  description: string;
  condition?: string;
  grade?: string;
  /**
   * Snapshot of the listing's first image AS STORED — this value is persisted
   * verbatim into `order_items.image_url`, so it must stay raw (display !=
   * persistence, handoff §9). `thumbFromLine` on the producer side must NOT
   * resolve; the "my orders" surface resolves at its own sinks.
   */
  imageUrl: RawImageRef;
  sellerId: string;
  sellerName: string;
  /** `profiles.avatar_url` as stored — see `imageUrl` above. */
  sellerAvatarUrl: RawImageRef;
  selectedShippingMethod: CheckoutPayloadSelectedShippingMethod | null;
}

/**
 * Per-line item carried on the CAPTURE channel under the flat_rate branch.
 * Structural extension of {@link CheckoutIntentFlatRateItem} (which carries
 * `listingId` / `quantity` / `shippingMethodId` / `context`) with the
 * {@link CheckoutPayloadItemSnapshot} rich snapshot fields.
 */
export type CheckoutPayloadFlatRateItem = CheckoutIntentFlatRateItem &
  CheckoutPayloadItemSnapshot;

/**
 * Per-line item carried on the CAPTURE channel under the live_rate branch.
 * Structural extension of {@link CheckoutIntentLiveRateItem} (which carries
 * `listingId` / `quantity` / `context`) with the
 * {@link CheckoutPayloadItemSnapshot} rich snapshot fields. The
 * `selectedShippingMethod` field is `null` under live_rate (the rate lives
 * at intent root via `externalShipping`).
 */
export type CheckoutPayloadLiveRateItem = CheckoutIntentLiveRateItem &
  CheckoutPayloadItemSnapshot;

/**
 * Back-compat alias — the union of both per-branch item shapes. Under PR-E
 * Move 4 the items array is narrowed by the root `kind` discriminator: the
 * flat_rate variant carries `CheckoutPayloadFlatRateItem[]`; the live_rate
 * variant carries `CheckoutPayloadLiveRateItem[]`. New code reads
 * `payload.items[i]` after narrowing on `payload.kind`.
 */
export type CheckoutPayloadItem =
  | CheckoutPayloadFlatRateItem
  | CheckoutPayloadLiveRateItem;

/**
 * Flat-rate checkout — the buyer pre-selected a shipping_method at cart
 * time; `selectedShippingMethod` lives on each `CheckoutPayloadItem`. No
 * `externalShipping` field exists on this variant — `tsc` would reject the
 * access. This is the MISU win.
 *
 * PR-E Move 4 (Sibling-F · F2): structural extension of
 * {@link CheckoutIntentFlatRate} + {@link CaptureSnapshot}. The
 * {@link CheckoutPayloadExtendsIntent} probe enforces the extension at
 * compile time.
 */
export interface CheckoutPayloadFlatRate
  extends Omit<CheckoutIntentFlatRate, "items">,
    CaptureSnapshot {
  items: CheckoutPayloadFlatRateItem[];
}

/**
 * Live-rate checkout — the buyer locked a carrier rate at checkout (via
 * `LiveRateSubflow`). `externalShipping` is REQUIRED here (inherited from
 * {@link CheckoutIntentLiveRate} under Move 4); `carrierProvider`
 * discriminates which carrier integration issued the rate.
 *
 * The illegal state "live_rate without externalShipping" is unrepresentable
 * — that's the entire point of this file. Pre-MISU the field was an optional
 * `externalBySeller?: Record<...>` map on the flat shape, which let
 * `buildCheckoutPayload(...)` return live-rate payloads with the field
 * silently absent on every callsite the agent forgot to thread. See the
 * incident handoff for the bleed window.
 *
 * PR-E Move 4 (Sibling-F · F2): structural extension of
 * {@link CheckoutIntentLiveRate} + {@link CaptureSnapshot}. The Intent half
 * already carries `externalShipping` as REQUIRED (post-Move-2); Payload
 * inherits the requirement by extension.
 */
export interface CheckoutPayloadLiveRate
  extends Omit<CheckoutIntentLiveRate, "items">,
    CaptureSnapshot {
  items: CheckoutPayloadLiveRateItem[];
  carrierProvider: CarrierProvider;
}

/**
 * The CAPTURE-channel payload. Discriminated on `kind`.
 *
 * Construction discipline: ONLY ever build via `buildCheckoutPayload` in
 * `lib/checkout-payload.ts`. A hand-rolled literal at a click handler is a
 * Sibling-E fork (see CLAUDE.md § Silent NACK "canonical builder" rule).
 */
export type CheckoutPayload = CheckoutPayloadFlatRate | CheckoutPayloadLiveRate;

// Compile-time exhaustiveness pin — Payload's `kind` axis MUST be set-equal
// to the canonical {@link CHECKOUT_ITEM_KINDS} constant. Adding a value to
// the array without extending this union (or vice versa) fails the build.
type _AssertPayloadKindsCoverUnion =
  CheckoutPayload["kind"] extends (typeof CHECKOUT_ITEM_KINDS)[number]
    ? true
    : never;
type _AssertNoExtraPayloadKinds =
  (typeof CHECKOUT_ITEM_KINDS)[number] extends CheckoutPayload["kind"]
    ? true
    : never;
const _payloadKindPinCoverUnion: _AssertPayloadKindsCoverUnion = true;
const _payloadKindPinNoExtras: _AssertNoExtraPayloadKinds = true;
void _payloadKindPinCoverUnion;
void _payloadKindPinNoExtras;

// ---------------------------------------------------------------------------
// PR-E Move 4 (Sibling-F · F2) compile-link probe — the F2 source of truth.
//
// If a future edit drifts Payload from Intent on a shared field
// (`externalShipping`, `currency`, `shippingAddress`, `items[i].context`,
// `items[i].shippingMethodId`, etc.), the `extends` check resolves to `never`
// → the assignment to `true` fails → tsc red-lines at the probe line BEFORE
// any boundary parse or runtime test runs. That's the F2 guarantee.
//
// Note this is a strictly stronger guard than the F4 source-grep
// (`tests/unit/fitness-checkout-payload-mirrors-intent-money.test.ts`): the
// probe catches structural drift on ALL Intent fields, not just the
// money-named ones the grep targets. The F4 pin stays as belt-and-braces.
// ---------------------------------------------------------------------------
type _PayloadExtendsIntentFlatRate =
  CheckoutPayloadFlatRate extends CheckoutIntentFlatRate ? true : never;
type _PayloadExtendsIntentLiveRate =
  CheckoutPayloadLiveRate extends CheckoutIntentLiveRate ? true : never;
const _flatPayloadExtendsIntentCheck: _PayloadExtendsIntentFlatRate = true;
const _livePayloadExtendsIntentCheck: _PayloadExtendsIntentLiveRate = true;
void _flatPayloadExtendsIntentCheck;
void _livePayloadExtendsIntentCheck;

// ---------------------------------------------------------------------------
// Runtime parse — Zod schemas
//
// `pinned<Interface>()(schema)` enforces schema↔interface mutual
// assignability at compile time (see `./pin.ts`). Drift on either side
// fails `tsc --noEmit` at the export site with the mismatched types in
// the error.
//
// `z.discriminatedUnion("kind", ...)` is the Zod sibling of the type-level
// discriminated union — adding a new `kind` variant requires extending the
// union AND adding a branch schema; either omission fails tsc on `pinned`.
// ---------------------------------------------------------------------------

export const CheckoutPayloadSelectedShippingMethodSchema =
  pinned<CheckoutPayloadSelectedShippingMethod>()(
    z.object({
      id: z.string(),
      name: z.string(),
      price: z.number(),
      extraValue: z.number().optional(),
      allowCombinedShipping: z.boolean().optional(),
    }),
  );

export const CheckoutPayloadItemSnapshotSchema =
  pinned<CheckoutPayloadItemSnapshot>()(
    z.object({
      price: z.number(),
      currency: z.string(),
      name: z.string(),
      description: z.string(),
      condition: z.string().optional(),
      grade: z.string().optional(),
      imageUrl: rawImageRefField(),
      sellerId: z.string(),
      sellerName: z.string(),
      sellerAvatarUrl: rawImageRefField(),
      selectedShippingMethod:
        CheckoutPayloadSelectedShippingMethodSchema.nullable(),
    }),
  );

/**
 * Per-branch item schemas — Intent line schema MERGED with the Payload
 * capture-time snapshot. PR-E Move 4 (Sibling-F · F2).
 */
export const CheckoutPayloadFlatRateItemSchema =
  pinned<CheckoutPayloadFlatRateItem>()(
    CheckoutIntentFlatRateItemSchema.merge(CheckoutPayloadItemSnapshotSchema),
  );

export const CheckoutPayloadLiveRateItemSchema =
  pinned<CheckoutPayloadLiveRateItem>()(
    CheckoutIntentLiveRateItemSchema.merge(CheckoutPayloadItemSnapshotSchema),
  );

/**
 * Loose Payload item schema — back-compat union for callers parsing a bare
 * payload line. The branch-discriminated schemas above are the canonical
 * entries — root-level parsing happens via {@link CheckoutPayloadSchema}.
 */
export const CheckoutPayloadItemSchema = pinned<CheckoutPayloadItem>()(
  z.union([CheckoutPayloadFlatRateItemSchema, CheckoutPayloadLiveRateItemSchema]),
);

/**
 * Per-branch Payload schemas — Intent branch schema EXTENDED with the
 * `CaptureSnapshot` field shape and the per-branch items array. Zod's
 * discriminated union at root requires each branch to remain a `ZodObject`,
 * which `.extend(shape)` preserves (whereas `.merge()` over two unrelated
 * objects can lose the prototype). The {@link CheckoutPayloadExtendsIntent}
 * type-level probe above guarantees the result still structurally extends
 * the Intent half.
 */
export const CheckoutPayloadFlatRateSchema = pinned<CheckoutPayloadFlatRate>()(
  CheckoutIntentFlatRateSchema.extend({
    ...captureSnapshotShape,
    items: z.array(CheckoutPayloadFlatRateItemSchema).min(1),
  }),
);

export const CheckoutPayloadLiveRateSchema = pinned<CheckoutPayloadLiveRate>()(
  CheckoutIntentLiveRateSchema.extend({
    ...captureSnapshotShape,
    items: z.array(CheckoutPayloadLiveRateItemSchema).min(1),
    externalShipping: CheckoutIntentExternalShippingSelectionSchema,
    // CarrierProvider is a const-derived literal union (see ./carrier.ts);
    // use z.literal() of the only current value to keep the schema strict.
    // Adding a new CarrierProvider extends both the type and this z.enum.
    carrierProvider: z.literal("shippo"),
  }),
);

export const CheckoutPayloadSchema = pinned<CheckoutPayload>()(
  z.discriminatedUnion("kind", [
    CheckoutPayloadFlatRateSchema,
    CheckoutPayloadLiveRateSchema,
  ]),
);

// Reference the imported `CaptureSnapshotSchema` symbol so the type-only
// `import` above stays valuable as a contract sanity probe — anyone deleting
// the canonical snapshot schema must also remove this re-export, which then
// red-lines downstream consumers.
export { CaptureSnapshotSchema };

// Re-export the canonical address type so consumers building a payload from
// the boundary-parsed Intent shape can satisfy both sides through one import.
// PR-E Move 1 (Sibling-F · F2): there is now ONE address shape across CREATE
// and CAPTURE channels (`CheckoutIntentAddress`); the pre-PR-E
// `CheckoutPayloadShippingInfo` was a parallel declaration with optionality
// drift — deleted.
export type { CheckoutIntentAddress };
